import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Users,
  Calendar,
  Clock,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Search,
  RefreshCw,
  Building2,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Star,
  Target,
  Zap,
  Award,
  ArrowUp,
  ArrowDown,
  Copy,
  X
} from 'lucide-react';
import KairaliAIIcon from './KairaliAIIcon';
import { useCachedData } from '../hooks/useCachedData';
import { useStickyFilter } from '../hooks/useStickyFilter';

// Completed tasks are moved out of MasterDelegation into a separate archive
// spreadsheet (~12.5k rows going back to 2022). Only a recent window is loaded,
// so a doer isn't suddenly shown years of historical completions.
const COMPLETED_WINDOW_WEEKS = 4;

const cleanHeaderName = (header) =>
  String(header)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

// The archive sheet labels a handful of columns differently from
// MasterDelegation. Mapping them onto the master names keeps the rest of this
// component — and the task modal — reading a single shape.
// Note: the archive's "Designation" column actually holds the task-created
// date, which is what MasterDelegation calls "Task Created Date".
const ARCHIVE_KEY_ALIASES = {
  designation: 'task_created_date',
  mode_of_submission: 'mode_of_task_submission',
  ea_director_remarks: 'ea_director_remarks_for_hold_to_review',
  whatsapp_3_sent_status_to_doer_completed: 'whatsapp_3_sent_status_to_doer_final_verify',
  sent_to_shifted_sheet_pending_status: 'sent_to_shifted_sheet_pending_hold_status',
  sent_to_revision_sheet_status: 'sent_to_initial_revision_sheet_status'
};

// rows[0] is the header row — the shape both /api/sheets and
// /api/delegation-completed return. Pass doerName to filter client-side;
// omit it when the rows arrived already filtered.
const rowsToTaskObjects = (rows, { aliases = null, source, firstSheetRow = null, doerName = null }) => {
  const headers = rows[0] || [];

  let keptRows = rows.slice(1);
  if (doerName !== null) {
    const doerNameIndex = headers.findIndex(
      (header) => header && String(header).toLowerCase().includes('doer name')
    );
    if (doerNameIndex === -1) {
      throw new Error('Doer Name column not found');
    }
    keptRows = keptRows.filter((row) => String(row[doerNameIndex] || '').trim() === doerName);
  }

  return keptRows.map((row, index) => {
    const task = {};

    headers.forEach((header, colIndex) => {
      if (!header || row[colIndex] === undefined) return;
      const key = cleanHeaderName(header);
      task[aliases?.[key] ?? key] = row[colIndex];
    });

    task.id = index + 1;
    task._source = source;
    // Only master rows carry rowNumber: it is what DataManager keys new-task
    // detection on, and archive rows must not collide with it.
    if (firstSheetRow !== null) {
      task.rowNumber = firstSheetRow + index;
    }

    return task;
  });
};

const fetchActiveDelegationTasks = async (doerName) => {
  const params = new URLSearchParams({
    sheetId: process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_DELEGATION,
    sheetName: process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_DELEGATION,
    range: 'A8:BZ'
  });

  const response = await fetch(`/api/sheets?${params}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  const rows = data.values || [];
  if (rows.length === 0) return [];

  // Header sits on sheet row 8, so the first data row is 9.
  return rowsToTaskObjects(rows, { source: 'master', firstSheetRow: 9, doerName });
};

const fetchCompletedDelegationTasks = async (doerName) => {
  const params = new URLSearchParams({
    doer: doerName,
    weeks: String(COMPLETED_WINDOW_WEEKS)
  });

  const response = await fetch(`/api/delegation-completed?${params}`);
  if (!response.ok) {
    let detail = `HTTP error! status: ${response.status}`;
    try {
      const body = await response.json();
      if (body.error) {
        detail = body.details ? `${body.error} — ${body.details}` : body.error;
      }
    } catch (parseError) {
      // Response wasn't JSON; the status line above is the best detail we have.
      console.warn('Completed delegation error response was not JSON:', parseError);
    }
    throw new Error(detail);
  }

  const data = await response.json();
  const rows = data.values || [];

  return {
    // Already filtered to this doer server-side.
    tasks: rows.length > 1
      ? rowsToTaskObjects(rows, { aliases: ARCHIVE_KEY_ALIASES, source: 'archive' })
      : [],
    truncated: Boolean(data.truncated),
    weeks: data.window?.weeks ?? COMPLETED_WINDOW_WEEKS
  };
};

// Portal Modal Component - Renders outside of dashboard container
const TaskModal = ({ task, onClose }) => {
  // Handle escape key and body scroll
  useEffect(() => {
    // Prevent body scroll
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px'; // Prevent layout shift
    
    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    // Cleanup
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Get task data safely
  const getTaskTitle = (task) => task?.task || task?.title || 'Untitled Task';
  const getDelegationId = (task) => task?.did || task?.delegation_id || task?.id || 'N/A';
  const getTaskCompany = (task) => task?.company || 'Unknown';
  const getTaskStatus = (task) => task?.delegation_status || task?.status || 'Pending';
  const getTaskDate = (task) => task?.task_created_date || task?.created_date || 'N/A';
  const getDueDate = (task) => task?.final_date || task?.due_date || 'Not specified';
  const getSubmissionLink = (task) => task?.submission_link || '';
  const getTaskRemarks = (task) => task?.all_remarks_history || task?.remarks || '';
  const getDoerResponseStatus = (task) => task?.response_status_from_doer || task?.doer_response_status || '';

  // Get scoring values
  const getEaseToImplement = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key => 
      key.toLowerCase().includes('easiest')
    );
    return parseFloat(task?.[possibleKeys] || 0);
  };

  const getRelativeImpact = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key => 
      (key.toLowerCase().includes('relative') && key.toLowerCase().includes('impact')) ||
      (key.toLowerCase().includes('impact') && key.toLowerCase().includes('result'))
    );
    return parseFloat(task?.[possibleKeys] || 0);
  };

  const getUrgency = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key => 
      key.toLowerCase().includes('urgency')
    );
    return parseFloat(task?.[possibleKeys] || 0);
  };

  const getImportance = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key => 
      key.toLowerCase().includes('importance')
    );
    return parseFloat(task?.[possibleKeys] || 0);
  };

  const getROTI = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key => 
      key.toLowerCase().includes('roti')
    );
    return parseFloat(task?.[possibleKeys] || 0);
  };

  const get8020Score = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key => 
      key.includes('80') || key.includes('20') || key.toLowerCase().includes('eighty')
    );
    return parseFloat(task?.[possibleKeys] || 0);
  };

  const getParetoStatus = (task) => {
    const possibleKeys = Object.keys(task || {}).find(key =>
      key.toLowerCase().includes('pareto') ||
      key.toLowerCase().includes('80') ||
      key.toLowerCase().includes('20')
    );

    const paretoValue = task?.[possibleKeys];

    if (typeof paretoValue === 'boolean') return paretoValue;
    if (typeof paretoValue === 'string') return paretoValue.toLowerCase() === 'true';
    if (typeof paretoValue === 'number') return paretoValue === 1;

    return false;
  };

  const score      = get8020Score(task);
  const isAiFocus  = getParetoStatus(task);
  const circumference = 2 * Math.PI * 36; // r=36
  const metrics = [
    { label: 'Ease',       value: getEaseToImplement(task), max: 10 },
    { label: 'Impact',     value: getRelativeImpact(task),  max: 10 },
    { label: 'Urgency',    value: getUrgency(task),         max: 5  },
    { label: 'Importance', value: getImportance(task),      max: 5  },
    { label: 'ROTI',       value: getROTI(task),            max: 3  },
  ];

  const modalContent = (
    <div className="k-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="k-modal" onClick={e => e.stopPropagation()}>

        {/* ── Slim header bar ── */}
        <div className="k-modal-header">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-xs font-semibold uppercase tracking-widest flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
              Delegation
            </span>
            <span style={{ color: 'var(--border-strong)' }}>·</span>
            <span className="text-xs font-mono truncate" style={{ color: 'var(--text-muted)' }}>
              {getDelegationId(task)}
            </span>
            {isAiFocus && (
              <span className="k-badge-ai flex-shrink-0">🎯 AI Focus</span>
            )}
          </div>
          <button onClick={onClose} className="k-modal-close flex-shrink-0"
            style={{ background: 'var(--bg-surface-2)', color: 'var(--text-muted)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Two-panel body ── */}
        <div className="k-modal-body-split">

          {/* LEFT — Score sidebar */}
          <div className="k-modal-aside">

            {/* Score arc */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <svg width="100" height="100" viewBox="0 0 100 100" style={{ overflow: 'visible' }}>
                {/* Track */}
                <circle cx="50" cy="50" r="36" fill="none"
                  stroke="var(--border)" strokeWidth="7" />
                {/* Progress */}
                <circle cx="50" cy="50" r="36" fill="none"
                  stroke="var(--gold-primary)" strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * circumference} ${circumference}`}
                  transform="rotate(-90 50 50)"
                  style={{ transition: 'stroke-dasharray 0.6s ease' }}
                />
                {/* Score number */}
                <text x="50" y="46" textAnchor="middle" fontSize="22" fontWeight="800"
                  fill="var(--gold-primary)" fontFamily="'Plus Jakarta Sans', sans-serif">
                  {score}
                </text>
                <text x="50" y="62" textAnchor="middle" fontSize="10" fontWeight="500"
                  fill="var(--text-muted)" fontFamily="'Plus Jakarta Sans', sans-serif">
                  /100
                </text>
              </svg>
              <p className="text-xs font-semibold uppercase tracking-wider text-center" style={{ color: 'var(--text-muted)' }}>
                80/20 Score
              </p>
            </div>

            <div style={{ height: 1, background: 'var(--border)' }} />

            {/* Metric bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {metrics.map(m => (
                <div key={m.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{m.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>{m.value}<span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>/{m.max}</span></span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${(m.value / m.max) * 100}%`,
                      background: m.value / m.max >= 0.8 ? 'var(--c-success)' : m.value / m.max >= 0.5 ? 'var(--gold-primary)' : 'var(--teal-primary)',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: 'var(--border)' }} />

            {/* Status badges */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="k-badge-pending" style={{ justifyContent: 'center' }}>
                {getTaskStatus(task)}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 600, textAlign: 'center', padding: '4px 0',
                color: 'var(--text-muted)',
              }}>
                {getTaskCompany(task)}
              </span>
            </div>
          </div>

          {/* RIGHT — Details */}
          <div className="k-modal-main">

            {/* Title */}
            <div>
              <h3 style={{
                fontSize: 15, fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.2px',
                color: 'var(--text-primary)',
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                margin: 0,
              }}>
                {getTaskTitle(task)}
              </h3>
            </div>

            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Delegation ID', value: getDelegationId(task) },
                { label: 'Company',       value: getTaskCompany(task) },
                { label: 'Created',       value: getTaskDate(task) },
                { label: 'Due Date',      value: getDueDate(task) || '—' },
                ...(getDoerResponseStatus(task) ? [{ label: 'Doer Response', value: getDoerResponseStatus(task) }] : []),
              ].map(({ label, value }) => (
                <div key={label} className="k-info-block" style={{ padding: '10px 12px' }}>
                  <span className="k-field-label" style={{ marginBottom: 3 }}>{label}</span>
                  <span className="k-field-value" style={{ fontSize: 13 }}>{value}</span>
                </div>
              ))}

              {getSubmissionLink(task) && (
                <div className="k-info-block" style={{ padding: '10px 12px' }}>
                  <span className="k-field-label" style={{ marginBottom: 3 }}>Submission</span>
                  <a href={getSubmissionLink(task)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1"
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--gold-primary)' }}
                    onClick={e => e.stopPropagation()}>
                    <ExternalLink className="w-3 h-3" /> View link
                  </a>
                </div>
              )}
            </div>

            {/* Remarks */}
            {getTaskRemarks(task) && (
              <div style={{ flex: 1, minHeight: 0 }}>
                <p className="k-section-title" style={{ marginBottom: 6 }}>Remarks</p>
                <p className="k-remarks" style={{ fontSize: 12, lineHeight: 1.6, maxHeight: 120, overflowY: 'auto' }}>
                  {getTaskRemarks(task)}
                </p>
              </div>
            )}

            {/* AI note */}
            <div style={{
              padding: '10px 14px', borderRadius: 10,
              background: isAiFocus ? 'var(--c-purple-bg)' : 'var(--bg-surface-3)',
              border: `1px solid ${isAiFocus ? 'rgba(107,63,160,0.22)' : 'var(--border)'}`,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{isAiFocus ? '🎯' : '📋'}</span>
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: isAiFocus ? 'var(--c-purple)' : 'var(--text-primary)', margin: 0 }}>
                  {isAiFocus ? 'AI Focus Priority' : 'Standard Priority'}
                </p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, marginTop: 1 }}>
                  {isAiFocus ? 'High impact relative to effort — prioritise this task' : 'Complete after AI Focus tasks'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="k-modal-footer">
          <button onClick={onClose} className="k-btn-outline" style={{ padding: '7px 16px' }}>Close</button>
          {getSubmissionLink(task) && (
            <a href={getSubmissionLink(task)} target="_blank" rel="noopener noreferrer" className="k-btn-gold">
              <ExternalLink className="w-4 h-4" /> View Submission
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const DelegationTasks = ({ currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_date');
  const [sortOrder, setSortOrder] = useState('desc'); // High to low priority
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState(new Set());

  const toggleExpanded = (e, index) => {
    e.stopPropagation();
    setExpandedTasks(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  // Active tasks from the master sheet, plus recently completed tasks from the
  // archive sheet they get moved into once done.
  const fetchDelegationData = useCallback(async () => {
    const [activeResult, completedResult] = await Promise.allSettled([
      fetchActiveDelegationTasks(currentUser.name),
      fetchCompletedDelegationTasks(currentUser.name)
    ]);

    // The active list is the point of this tab — if it fails, fail loudly.
    if (activeResult.status === 'rejected') {
      console.error('Error fetching delegation data:', activeResult.reason);
      throw activeResult.reason;
    }

    const activeTasks = activeResult.value;
    const meta = {
      error: null,
      truncated: false,
      weeks: COMPLETED_WINDOW_WEEKS,
      count: 0
    };
    let completedTasks = [];

    if (completedResult.status === 'fulfilled') {
      completedTasks = completedResult.value.tasks;
      meta.truncated = completedResult.value.truncated;
      meta.weeks = completedResult.value.weeks;
      meta.count = completedTasks.length;
    } else {
      // Completed history is supplementary; never let it take the tab down.
      console.error('Error fetching completed delegation history:', completedResult.reason);
      meta.error = completedResult.reason?.message || 'Could not reach the completed delegation sheet';
    }

    // Master and archive should be disjoint — a task lives in one or the other.
    // Prefer the active row if a move happens to be mid-flight.
    const activeDids = new Set(activeTasks.map(task => task.did).filter(Boolean));
    const merged = activeTasks.concat(
      completedTasks.filter(task => !task.did || !activeDids.has(task.did))
    );

    // Rides along on the array so it survives DataManager caching and remounts.
    merged._completedMeta = meta;

    return merged;
  }, [currentUser.name]);

  // Use cached data hook
  const { data: tasks, loading, error, refresh:originalRefresh, lastRefresh } = useCachedData(
    'delegation', 
    currentUser, 
    fetchDelegationData
  );

  // Add local loading state for manual refresh
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();

  // Rides on the tasks array, so this is correct on a fresh fetch and on a
  // cache hit alike.
  const completedMeta = tasks?._completedMeta || null;
  const completedWeeks = completedMeta?.weeks ?? COMPLETED_WINDOW_WEEKS;

  // Enhanced refresh function that ensures fresh data from server  
  const refresh = useCallback(async () => {
    console.log(`🔄 Force refreshing Delegation data for ${currentUser.name} - fetching fresh from server...`);
    
    setIsManualRefreshing(true);
    
    try {
      await originalRefresh(true); // Force refresh from server
      console.log(`✅ Successfully refreshed Delegation data for ${currentUser.name}`);
    } catch (error) {
      console.error(`❌ Failed to refresh Delegation data for ${currentUser.name}:`, error);
    } finally {
      setIsManualRefreshing(false);
    }
  }, [originalRefresh, currentUser.name]);

  // Priority scoring functions with improved column name matching
  const getEaseToImplement = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      key.toLowerCase().includes('easiest')
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const getRelativeImpact = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      (key.toLowerCase().includes('relative') && key.toLowerCase().includes('impact')) ||
      (key.toLowerCase().includes('impact') && key.toLowerCase().includes('result'))
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const getUrgency = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      key.toLowerCase().includes('urgency')
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const getImportance = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      key.toLowerCase().includes('importance')
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const getEmotionalEnergy = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      key.toLowerCase().includes('emotional') && key.toLowerCase().includes('energy')
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const getSignificance = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      key.toLowerCase().includes('saving') &&  key.toLowerCase().includes('future')
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const getROTI = (task) => {
    const possibleKeys = Object.keys(task).find(key => 
      key.toLowerCase().includes('roti')
    );
    return parseFloat(task[possibleKeys] || 0);
  };

  const get8020Score = useCallback((task) => {
    const possibleKeys = Object.keys(task).find(key =>
      key.includes('80') || key.includes('20') || key.toLowerCase().includes('eighty')
    );
    return parseFloat(task[possibleKeys] || 0);
  }, []);

  // Add this function after your other getter functions
  const getParetoStatus = (task) => {
    // Look for pareto column in task data
    const possibleKeys = Object.keys(task).find(key =>
      key.toLowerCase().includes('pareto') ||
      key.toLowerCase().includes('80') ||
      key.toLowerCase().includes('20')
    );

    const paretoValue = task[possibleKeys];

    // Handle various formats: true/false, "true"/"false", 1/0
    if (typeof paretoValue === 'boolean') return paretoValue;
    if (typeof paretoValue === 'string') return paretoValue.toLowerCase() === 'true';
    if (typeof paretoValue === 'number') return paretoValue === 1;

    return false; // Default to false if not found
  };

  // Calculate overall priority score based on 80/20 with new ranges
  const calculatePriorityLevel = (score) => {
    if (score >= 75) return 'urgent';
    if (score >= 50) return 'high';
    if (score >= 25) return 'medium';
    return 'low';
  };

  // Get short priority label
  const getShortPriorityLabel = (priority) => {
    switch (priority.toLowerCase()) {
      case 'urgent': return 'Urgent';
      case 'high': return 'High';
      case 'medium': return 'Medium';
      case 'low': return 'Low';
      default: return 'Unknown';
    }
  };

  const getTaskTitle = (task) => {
    return task.task || 'Untitled Task';
  };

  const getTaskCompany = (task) => {
    return task.company || 'Unknown';
  };

  // NEW: Function to get Delegation ID (DID)
  const getDelegationId = (task) => {
    return task.did || task.delegation_id || task.task_id || task.id || 'N/A';
  };

  // NEW: Function to check if task is created within last 7 days
  const isNewTask = (task) => {
    const taskDate = getTaskDate(task);
    if (!taskDate) return false;
    
    try {
      const today = new Date();
      let createdDate;
      
      // Handle different date formats
      if (taskDate.includes('/')) {
        // Handle DD/MM/YYYY or MM/DD/YYYY format
        const parts = taskDate.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          
          // Assume DD/MM/YYYY format (day first)
          createdDate = new Date(year, month - 1, day);
        } else {
          createdDate = new Date(taskDate);
        }
      } else {
        createdDate = new Date(taskDate);
      }
      
      if (isNaN(createdDate.getTime())) {
        console.log('Invalid date:', taskDate);
        return false;
      }
      
      const diffTime = today - createdDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays <= 2 && diffDays >= 0; // Within last 2 days
    } catch (error) {
      console.error('Error parsing task date:', taskDate, error);
      return false;
    }
  };

  const getTaskStatus = (task) => {
    return task.delegation_status || 'pending';
  };

  const getTaskPriority = useCallback((task) => {
    const score = get8020Score(task);
    return calculatePriorityLevel(score);
  }, [get8020Score]);

  const getTaskDate = (task) => {
    return task.task_created_date || task.first_date || new Date().toLocaleDateString();
  };

  const getDueDate = (task) => {
    return task.final_date || task.first_date || '';
  };

  const getSubmissionLink = (task) => {
    return task.submission_link || '';
  };

  // NEW: Function to get doer response status
  const getDoerResponseStatus = (task) => {
    return task.response_status_from_doer || task.doer_response_status || task.doer_status || '';
  };


  // NEW: Function to get doer response badge
  const getDoerResponseBadge = (task) => {
    const doerStatus = getDoerResponseStatus(task).toLowerCase().trim();
    
    if (doerStatus.includes('completed')) {
      return {
        text: 'Pending Review',
        className: 'bg-green-100 text-green-800 border-green-200'
      };
    }
    if (doerStatus.includes('date to be revised') || doerStatus.includes('revision')) {
      return {
        text: 'Revision Needed',
        className: 'bg-orange-100 text-orange-800 border-orange-200'
      };
    }
    if (doerStatus.includes('in progress') || doerStatus.includes('working')) {
      return {
        text: 'In Progress',
        className: 'bg-blue-100 text-blue-800 border-blue-200'
      };
    }
    
    return {
      text: 'Not Started',
      className: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  };

  // Check if task is overdue
  const isOverdue = (task) => {
    const dueDate = getDueDate(task);
    if (!dueDate) return false;
    const today = new Date();
    const taskDueDate = new Date(dueDate);
    return taskDueDate < today;
  };

  // Enhanced filtering and sorting
  const filteredAndSortedTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];

    let filtered = tasks.filter(task => {
      const matchesSearch = searchTerm === '' || 
        getTaskTitle(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getTaskCompany(task).toLowerCase().includes(searchTerm.toLowerCase());
      
      // 'active' hides completed tasks; 'all' deliberately includes them.
      const status = getTaskStatus(task).toLowerCase();
      let matchesStatus;
      if (statusFilter === 'active') {
        matchesStatus = !status.includes('completed');
      } else if (statusFilter === 'all') {
        matchesStatus = true;
      } else if (statusFilter === 'overdue') {
        // Overdue is derived, not a Delegation Status value. Completed work is
        // never overdue, which keeps this in step with the Overdue stat card.
        matchesStatus = !status.includes('completed') && isOverdue(task);
      } else {
        matchesStatus = status.includes(statusFilter);
      }
      
      const taskPriority = getTaskPriority(task);
      let matchesPriority;
      if (priorityFilter === 'all') {
        matchesPriority = true;
      } else if (priorityFilter === 'ai_focus') {
        matchesPriority = getParetoStatus(task);
      } else {
        matchesPriority = taskPriority.toLowerCase().includes(priorityFilter);
      }
      
      return matchesSearch && matchesStatus && matchesPriority;
    });

    // Sort tasks
    filtered.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'ai_focus':
          valueA = getParetoStatus(a);
          valueB = getParetoStatus(b);
          break;
        case '80_20':
          valueA = get8020Score(a);
          valueB = get8020Score(b);
          break;
        case 'ease':
          valueA = getEaseToImplement(a);
          valueB = getEaseToImplement(b);
          break;
        case 'impact':
          valueA = getRelativeImpact(a);
          valueB = getRelativeImpact(b);
          break;
        case 'urgency':
          valueA = getUrgency(a);
          valueB = getUrgency(b);
          break;
        case 'importance':
          valueA = getImportance(a);
          valueB = getImportance(b);
          break;
        case 'roti':
          valueA = getROTI(a);
          valueB = getROTI(b);
          break;
        case 'created_date':
          valueA = new Date(getTaskDate(a));
          valueB = new Date(getTaskDate(b));
          break;
        case 'due_date':
          // Handle null/empty due dates by treating them as far in the future
          valueA = getDueDate(a) ? new Date(getDueDate(a)).getTime() : (sortOrder === 'asc' ? Infinity : -Infinity);
          valueB = getDueDate(b) ? new Date(getDueDate(b)).getTime() : (sortOrder === 'asc' ? Infinity : -Infinity);
          break;
        default:
          valueA = getTaskTitle(a);
          valueB = getTaskTitle(b);
      }

      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchTerm, statusFilter, priorityFilter, sortBy, sortOrder, getTaskPriority]);

  const getTaskStats = () => {
    if (!Array.isArray(tasks)) return { active: 0, completed: 0, pending: 0, hold: 0, overdue: 0, paretoTasks: 0 };

    const isCompleted = task => getTaskStatus(task).toLowerCase().includes('completed');
    // Completed tasks come from a rolling window, so counting them alongside
    // the active ones would make every other card look like a share of them.
    // Every card below 'Completed' describes active work only.
    const activeTasks = tasks.filter(task => !isCompleted(task));

    return {
      active: activeTasks.length,
      completed: tasks.filter(isCompleted).length,
      pending: activeTasks.filter(task =>
        getTaskStatus(task).toLowerCase().includes('pending')).length,
      hold: activeTasks.filter(task =>
        getTaskStatus(task).toLowerCase().includes('hold')).length,
      overdue: activeTasks.filter(task => isOverdue(task)).length,
      paretoTasks: activeTasks.filter(task => getParetoStatus(task)).length
    };
  };

  const stats = getTaskStats();

  const copyTasksToClipboard = () => {
    const formatDate = (dateString) => {
      if (!dateString) return '';

      let date;
      const parts = dateString.split(/[-/]/);

      if (parts.length === 3) {
        const part1 = parseInt(parts[0], 10);
        const part2 = parseInt(parts[1], 10);
        const part3 = parseInt(parts[2], 10);

        if (isNaN(part1) || isNaN(part2) || isNaN(part3)) {
            // Handle non-numeric parts, likely 'DD-Mon-YYYY'
            date = new Date(dateString);
        } else {
          // Handle numeric dates
          if (part1 > 12 && part1 <= 31) {
            // DD/MM/YYYY
            date = new Date(part3, part2 - 1, part1);
          } else if (part2 > 12 && part2 <= 31) {
            // MM/DD/YYYY - this is unlikely in this context, but good to have
             date = new Date(part3, part1 - 1, part2);
          }
          else {
            // Assume MM/DD/YYYY for ambiguous dates like 01/02/2025
            date = new Date(part3, part1 - 1, part2);
          }
        }
      } else {
        date = new Date(dateString);
      }

      if (!date || isNaN(date.getTime())) {
        return dateString; // Return original string if parsing fails
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('default', { month: 'short' }).toLowerCase();
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const header = [
      "Task Description",
      "Final Date",
      "Ease to Implement (1–10)",
      "Relative Impact on Results (1–10)",
      "Urgency (1–5)",
      "Importance (1–5)",
      "Emotional Energy Required (1–5)",
      "Significance (1–5)",
      "ROTI (1–3)",
      "Assigned To (Team Member, Optional)",
      "Blocking Dependencies (Optional)"
    ].join("\t");

    const rows = filteredAndSortedTasks.map(task => {
      return [
        getTaskTitle(task),
        formatDate(getDueDate(task)),
        getEaseToImplement(task),
        getRelativeImpact(task),
        getUrgency(task),
        getImportance(task),
        getEmotionalEnergy(task),
        getSignificance(task),
        getROTI(task),
        "", // Assigned To
        ""  // Blocking Dependencies
      ].join("\t");
    });

    const clipboardText = [header, ...rows].join("\n");
    navigator.clipboard.writeText(clipboardText);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (loading && !isManualRefreshing) {
    return (
      <div className="k-card">
        <div className="k-loading">
          <div className="k-loading-spinner" />
          <p className="k-loading-text">Loading delegation tasks…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-card">
        <div className="k-error">
          <div className="k-error-icon"><AlertTriangle className="w-7 h-7" /></div>
          <p className="k-error-title">Error Loading Tasks</p>
          <p className="k-error-msg">{error}</p>
          <button onClick={refresh} className="k-btn-primary mt-2">
            <RefreshCw className="w-4 h-4" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page">

      {/* ── Completed-history notices (never block the active list) ── */}
      {completedMeta?.error && (
        <div className="k-notice">
          <AlertTriangle className="w-4 h-4 k-notice-icon" />
          <div className="k-notice-body">
            <p className="k-notice-title">Completed history unavailable</p>
            <p className="k-notice-msg">
              {`Your active tasks below are up to date, but completed tasks from the last ${completedWeeks} weeks could not be loaded — ${completedMeta.error}`}
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={isManualRefreshing}
            title={isManualRefreshing ? 'Retrying…' : 'Reload completed history'}
            className="k-refresh-btn flex-shrink-0"
            style={{ cursor: isManualRefreshing ? 'not-allowed' : 'pointer' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isManualRefreshing ? 'animate-spin' : ''}`} />
            <span>{isManualRefreshing ? 'Retrying…' : 'Retry'}</span>
          </button>
        </div>
      )}

      {completedMeta?.truncated && !completedMeta.error && (
        <div className="k-notice">
          <AlertTriangle className="w-4 h-4 k-notice-icon" />
          <div className="k-notice-body">
            <p className="k-notice-title">Completed history partially loaded</p>
            <p className="k-notice-msg">
              {`There were too many completed tasks in the last ${completedWeeks} weeks to load at once, so the oldest ones are not shown.`}
            </p>
          </div>
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div className="k-stats-grid">
        {[
          { label: 'Active',    value: stats.active,      icon: Users,         color: 'var(--teal-primary)',   bg: 'var(--teal-dim)',       tier: stats.active === 0 ? 'tier-muted' : '',                             status: 'active',    priority: 'all' },
          { label: `Completed (${completedWeeks}w)`, value: stats.completed, icon: CheckCircle, color: 'var(--c-success)', bg: 'var(--c-success-bg)', tier: stats.completed === 0 ? 'tier-muted' : 'tier-success', status: 'completed', priority: 'all' },
          { label: 'Hold',      value: stats.hold,        icon: Clock,         color: 'var(--c-info)',         bg: 'var(--c-info-bg)',      tier: stats.hold === 0 ? 'tier-muted' : '',                               status: 'hold',      priority: 'all' },
          { label: 'Pending',   value: stats.pending,     icon: AlertCircle,   color: 'var(--c-warning)',      bg: 'var(--c-warning-bg)',   tier: stats.pending === 0 ? 'tier-muted' : 'tier-warning',                status: 'pending',   priority: 'all' },
          { label: 'Overdue',   value: stats.overdue,     icon: AlertTriangle, color: 'var(--c-danger)',       bg: 'var(--c-danger-bg)',    tier: stats.overdue === 0 ? 'tier-muted' : 'tier-urgent',                 status: 'overdue',   priority: 'all' },
          { label: 'AI Focus',  value: stats.paretoTasks, icon: Target,        color: 'var(--c-purple)',       bg: 'var(--c-purple-bg)',    tier: stats.paretoTasks === 0 ? 'tier-muted' : '',                        status: 'active',    priority: 'ai_focus' },
        ].map(({ label, value, icon: Icon, color, bg, tier, status, priority }) => {
          const isSelected = statusFilter === status && priorityFilter === priority;
          return (
            <button
              key={label}
              type="button"
              aria-pressed={isSelected}
              title={isSelected ? `Showing ${label} — click to clear` : `Show ${label} tasks`}
              onClick={() => {
                // Clicking the selected card again clears back to the default view.
                if (isSelected) {
                  setStatusFilter('active');
                  setPriorityFilter('all');
                } else {
                  setStatusFilter(status);
                  setPriorityFilter(priority);
                }
              }}
              className={`k-stat-card w-full text-left ${tier} ${isSelected ? 'selected' : ''}`}
            >
              <div className="k-stat-icon" style={{ background: bg }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <div>
                <p className="k-stat-label">{label}</p>
                <p className="k-stat-value" style={{ color }}>{value}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
      <div className={`k-card p-4 k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`}>
        <div className="k-filter-bar">
          <div className="k-search-wrap">
            <Search className="k-search-icon" />
            <input
              type="text"
              placeholder="Search tasks…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="k-search"
            />
          </div>
          <button
            onClick={refresh}
            disabled={isManualRefreshing}
            title={lastRefresh ? `Refresh data · Last updated ${new Date(lastRefresh).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Refresh data'}
            className="k-refresh-btn w-8 h-8 flex-shrink-0 px-0 justify-center"
            style={{ cursor: isManualRefreshing ? 'not-allowed' : 'pointer' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isManualRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="k-select">
            <option value="active">Active Tasks</option>
            <option value="pending">Pending</option>
            <option value="hold">Hold</option>
            <option value="overdue">Overdue</option>
            <option value="completed">{`Completed · last ${completedWeeks} weeks`}</option>
            <option value="all">All (incl. completed)</option>
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="k-select">
            <option value="all">All Priority</option>
            <option value="ai_focus">AI Focus</option>
            <option value="urgent">Urgent (75–100)</option>
            <option value="high">High (50–75)</option>
            <option value="medium">Medium (25–50)</option>
            <option value="low">Low (0–25)</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="k-select">
            <option value="ai_focus">Sort by AI Focus</option>
            <option value="80_20">Sort by 80/20 Score</option>
            <option value="ease">Ease to Implement</option>
            <option value="impact">Relative Impact</option>
            <option value="urgency">Urgency</option>
            <option value="importance">Importance</option>
            <option value="roti">ROTI</option>
            <option value="created_date">Created Date</option>
            <option value="due_date">Due Date</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="k-refresh-btn px-3"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
          </button>
          <button
            onClick={copyTasksToClipboard}
            disabled={isCopied}
            className={isCopied ? 'k-btn-primary px-3 py-2' : 'k-refresh-btn px-3'}
          >
            <Copy className="w-4 h-4" />
            <span>{isCopied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
      </div>

      {/* ── Task List ── */}
      <div className="k-card overflow-hidden">
        {filteredAndSortedTasks.length === 0 ? (
          <div className="k-empty">
            <div className="k-empty-icon"><Users className="w-7 h-7" /></div>
            <p className="k-empty-title">No Tasks Found</p>
            <p className="k-empty-msg">
              {statusFilter === 'completed' && stats.completed === 0
                ? `Nothing completed in the last ${completedWeeks} weeks. Older completed tasks aren't loaded.`
                : tasks.length === 0
                  ? `No delegation tasks assigned to ${currentUser.name}`
                  : 'No tasks match your current filters'}
            </p>
          </div>
        ) : (
          <div className="k-task-list">
            {filteredAndSortedTasks.map((task, index) => {
              const doerStatus = getDoerResponseStatus(task).toLowerCase().trim();
              // Delegation Status is authoritative: 'Completed' means management
              // has reviewed and closed the task, so the doer-response column
              // ("Completed" → awaiting review) no longer says anything useful.
              const isTaskCompleted = getTaskStatus(task).toLowerCase().includes('completed');
              let rowClass = 'k-task-row';
              if (isTaskCompleted)                       rowClass += ' completed';
              else if (doerStatus.includes('completed')) rowClass += ' completed';
              else if (doerStatus.includes('revision'))  rowClass += ' revision';
              else if (doerStatus.includes('progress'))  rowClass += ' in-progress';
              else {
                const p = getTaskPriority(task);
                if (p === 'urgent') rowClass += ' urgent';
                else if (p === 'high') rowClass += ' high';
                else if (p === 'medium') rowClass += ' medium';
                else rowClass += ' low';
              }

              return (
                <div
                  key={index}
                  className={rowClass}
                  onClick={() => { setSelectedTask(task); setShowModal(true); }}
                >
                  <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex flex-wrap items-start gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className={`k-task-title ${expandedTasks.has(index) ? '' : 'line-clamp-2'}`}>
                          {getTaskTitle(task)}
                        </h4>
                        {getTaskTitle(task).length > 120 && (
                          <button
                            onClick={(e) => toggleExpanded(e, index)}
                            className="text-[11px] font-semibold mt-0.5"
                            style={{ color: 'var(--gold-primary)' }}
                          >
                            {expandedTasks.has(index) ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0">
                        {isNewTask(task) && (
                          <span className="k-badge-new">
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.85)', display: 'inline-block', flexShrink: 0 }} />
                            NEW
                          </span>
                        )}
                        <span className={`k-badge-${getTaskPriority(task)}`}>
                          {getShortPriorityLabel(getTaskPriority(task))}
                        </span>
                        <span className={
                          isTaskCompleted ? 'k-badge-completed' :
                          getTaskStatus(task).toLowerCase().includes('hold') ? 'k-badge-hold' :
                          'k-badge-pending'
                        }>
                          {getTaskStatus(task)}
                        </span>
                        {!isTaskCompleted && getDoerResponseStatus(task) && (
                          <span className={
                            doerStatus.includes('completed') ? 'k-badge-completed' :
                            doerStatus.includes('revision') ? 'k-badge-high' :
                            doerStatus.includes('progress') ? 'k-badge-progress' : 'k-badge-hold'
                          }>
                            {getDoerResponseBadge(task).text}
                          </span>
                        )}
                        <span className="k-badge-score">80/20: {get8020Score(task)}</span>
                        {getParetoStatus(task) && (
                          <span className="k-tooltip-wrap">
                            <span className="k-badge-ai">🎯 AI Focus</span>
                            <span className="k-tooltip">AI-prioritised: high impact relative to effort based on 80/20 scoring</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Meta row */}
                    <div className="k-task-meta">
                      <span className="k-task-meta-item">
                        <Building2 className="w-3.5 h-3.5" />
                        DID: {getDelegationId(task)}
                      </span>
                      <span className="k-task-meta-item">
                        <Calendar className="w-3.5 h-3.5" />
                        {getTaskDate(task)}
                      </span>
                      {getDueDate(task) && (
                        <span className="k-task-meta-item">
                          <Clock className="w-3.5 h-3.5" />
                          Due: {getDueDate(task)}
                        </span>
                      )}
                    </div>

                    {/* Scores */}
                    <div className="k-task-scores">
                      <span className="k-score-chip"><Zap className="w-3 h-3" />Ease: {getEaseToImplement(task)}/10</span>
                      <span className="k-score-chip"><TrendingUp className="w-3 h-3" />Impact: {getRelativeImpact(task)}/10</span>
                      <span className="k-score-chip"><AlertTriangle className="w-3 h-3" />Urgency: {getUrgency(task)}/5</span>
                      <span className="k-score-chip"><Star className="w-3 h-3" />Importance: {getImportance(task)}/5</span>
                      <span className="k-score-chip"><Award className="w-3 h-3" />ROTI: {getROTI(task)}/3</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 self-center">
                    {getSubmissionLink(task) && (
                      <a
                        href={getSubmissionLink(task)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Open submission link"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6,
                          background: 'rgba(200,146,42,0.12)', color: 'var(--gold-primary)',
                          border: '1px solid rgba(200,146,42,0.3)', flexShrink: 0,
                        }}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        window.dispatchEvent(new CustomEvent('ai-task-selected', {
                          detail: {
                            text: `Help with this task: "${getTaskTitle(task).substring(0, 120)}" (ID: ${getDelegationId(task)}, Due: ${getDueDate(task)}, Score: ${get8020Score(task)}/100, Status: ${getTaskStatus(task)}). What are the next steps to close this?`
                          }
                        }));
                      }}
                      title="Ask AI about this task"
                      className="k-ask-ai"
                    >
                      <KairaliAIIcon size={12} />
                      Ask AI
                    </button>
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => { setShowModal(false); setSelectedTask(null); }}
        />
      )}
    </div>
  );
};

export default DelegationTasks;
